/**
 * BOQ Tracker Page
 * Full-page view for managing a project's Materials Schedule / BOQ.
 * Uses an Excel-like spreadsheet for data entry and tracking.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, CircularProgress, Alert, Chip,
  Breadcrumbs, Link, Card, CardContent, Grid, Snackbar, Badge,
  Tabs, Tab, TextField, MenuItem
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DescriptionIcon from '@mui/icons-material/Description';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SendIcon from '@mui/icons-material/Send';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { brand, status as statusColors } from '../theme/colors';
import { useAuth } from '../contexts/AuthContext';
import boqService from '../services/boqService';
import notificationService from '../services/notificationService';
import BOQSpreadsheet from '../components/boq/BOQSpreadsheet';
import BOQSummaryTable from '../components/boq/BOQSummaryTable';
import BOQChangesReviewDialog from '../components/boq/BOQChangesReviewDialog';
import BOQUnmatchedBanner from '../components/boq/BOQUnmatchedBanner';
import BOQPurchaseHistory from '../components/boq/BOQPurchaseHistory';
import BOQCommentThread from '../components/boq/BOQCommentThread';
import QuoteRequestWizard from '../components/quotes/QuoteRequestWizard';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import api from '../services/api';

// Map BOQ item names onto the quote-request material categories so the
// merchant quote wizard can be prefilled from the schedule.
const QUOTE_CATEGORY_RULES = [
  ['bricks', /brick|block|paver/i],
  ['steel', /steel|rebar|reinforc|brc|mesh|angle iron|lintel/i],
  ['paint', /paint|varnish|sealer|undercoat|pva/i],
  ['roofing', /roof|ibr|chromadek|gutter|purlin|ridge|tile/i],
  ['timber', /timber|wood|plank|brander|door frame|truss/i],
  ['plumbing', /pipe|plumb|tap|valve|geyser|toilet|basin|sink|fitting/i],
  ['electrical', /electr|cable|conduit|socket|switch|db board|breaker|bulb|light/i],
  ['cement', /cement|concrete|mortar|plaster|sand|stone|aggregate/i],
];
const classifyQuoteCategory = (name = '') =>
  (QUOTE_CATEGORY_RULES.find(([, re]) => re.test(name)) || ['cement'])[0];

const fmt = (n) => parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const statusConfig = {
  draft: { label: 'Draft', color: statusColors.neutral, bg: '#f3f4f6' },
  pending_acknowledgement: { label: 'Pending Review', color: statusColors.warning, bg: statusColors.warningBg },
  active: { label: 'Active', color: statusColors.success, bg: statusColors.successBg },
  superseded: { label: 'Superseded', color: statusColors.neutral, bg: '#f3f4f6' }
};

export default function BOQTrackerPage() {
  const { projectId, boqId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [boqList, setBoqList] = useState([]);
  const [activeDoc, setActiveDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createMode, setCreateMode] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createTitle, setCreateTitle] = useState('');
  const [changesOpen, setChangesOpen] = useState(false);
  const [pendingChanges, setPendingChanges] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [unifiedSummary, setUnifiedSummary] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [allPurchases, setAllPurchases] = useState([]);
  const [workStages, setWorkStages] = useState([]);
  const [selectedStageId, setSelectedStageId] = useState('');
  const [createStageId, setCreateStageId] = useState('');
  const [comments, setComments] = useState([]);
  const [commentLoading, setCommentLoading] = useState(false);
  const [quoteWizardOpen, setQuoteWizardOpen] = useState(false);
  const [quoteProject, setQuoteProject] = useState(null);
  const [quoteMaterials, setQuoteMaterials] = useState(null);
  const [aiDraft, setAiDraft] = useState(null);
  const [aiDrafting, setAiDrafting] = useState(false);
  const [aiDraftError, setAiDraftError] = useState('');
  // Current doc id for socket-refresh checks without re-subscribing on each load
  const activeDocRef = React.useRef(null);
  const isContractor = user?.role === 'contractor';
  const isProjectManager = user?.role === 'project_manager';
  // The assigned PM manages the schedule like the contractor (backend enforces
  // that the PM is actually assigned to this project, so an unassigned PM can't
  // even load the list).
  const canManage = isContractor || isProjectManager;

  const showSnack = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  // ─── Load Data ───

  const loadBoqList = useCallback(async () => {
    try {
      setLoading(true);
      const docs = await boqService.getProjectBOQs(projectId, selectedStageId ? { milestone_id: selectedStageId } : {});
      setBoqList(docs);
    } catch (err) {
      console.error('BOQ list load error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load materials schedules');
    } finally {
      setLoading(false);
    }
  }, [projectId, selectedStageId]);

  const loadWorkStages = useCallback(async () => {
    try {
      const response = await boqService.getProjectStages(projectId);
      setWorkStages(response?.data || []);
    } catch (err) {
      setWorkStages([]);
    }
  }, [projectId]);

  const loadDocument = useCallback(async (id) => {
    try {
      setLoading(true);
      const [doc, changes, unified, thread] = await Promise.all([
        boqService.getDocument(id),
        boqService.getPendingChanges(id),
        boqService.getUnifiedSummary(id).catch(() => null),
        boqService.getComments(id).catch(() => [])
      ]);
      setActiveDoc(doc);
      activeDocRef.current = doc?.id || null;
      setPendingChanges(changes);
      setUnifiedSummary(unified);
      setComments(Array.isArray(thread) ? thread : []);
      // Load all purchases for the Purchase Log tab
      try {
        const purchases = await boqService.getDocumentPurchases(id);
        setAllPurchases(purchases);
      } catch (e) { /* ok */ }
    } catch (err) {
      setError('Failed to load document');
    } finally {
      setLoading(false);
    }
  }, []);

  // Live updates: join the project socket room and refresh when the other
  // party changes the schedule (the backend emits boq-* events to the room).
  useEffect(() => {
    if (!projectId) return undefined;
    notificationService.joinProject(projectId);

    const refresh = (data) => {
      if (!data?.boqId) return;
      if (activeDocRef.current && data.boqId === activeDocRef.current) {
        loadDocument(data.boqId);
      }
    };

    const unsubscribers = [
      'boq-updated', 'boq-acknowledged', 'boq-change',
      'boq-purchase', 'boq-labor-payment', 'boq-comment',
    ].map((event) => notificationService.on(event, refresh));

    return () => unsubscribers.forEach((unsub) => unsub());
  }, [projectId, loadDocument]);

  useEffect(() => {
    loadWorkStages();
    if (boqId === 'new') {
      setCreateMode(true);
      if (selectedStageId && !createStageId) setCreateStageId(selectedStageId);
      setLoading(false);
    } else if (boqId) {
      loadDocument(boqId);
    } else {
      loadBoqList();
    }
  }, [boqId, loadDocument, loadBoqList, loadWorkStages, selectedStageId, createStageId]);

  // ─── Create Mode ───

  const handleSaveNewSchedule = async (categories) => {
    if (!createTitle.trim()) {
      showSnack('Please enter a title', 'error');
      return;
    }
    setCreateLoading(true);
    try {
      const doc = await boqService.createDocument({
        project_id: projectId,
        milestone_id: createStageId || null,
        title: createTitle,
        categories: categories.map(c => ({
          name: c.name,
          labor_cost: c.labor_cost || 0,
          milestone_id: c.milestone_id || createStageId || null,
          items: (c.items || []).filter(i => i.item_name && i.quantity && i.unit_price).map(i => ({
            item_name: i.item_name,
            quantity: parseFloat(i.quantity),
            unit: i.unit || 'bags',
            unit_price: parseFloat(i.unit_price),
            notes: i.notes || ''
          }))
        })).filter(c => c.items.length > 0 || c.labor_cost > 0)
      });
      showSnack('Schedule created successfully');
      setCreateMode(false);
      navigate(`/projects/${projectId}/boq/${doc.id}`);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to create schedule';
      showSnack(msg, 'error');
    } finally {
      setCreateLoading(false);
    }
  };

  // ─── Edit Mode Handlers ───

  const handleUpdateItem = async (itemId, data) => {
    try {
      await boqService.updateItem(itemId, data);
      await loadDocument(activeDoc.id);
    } catch (err) {
      showSnack('Failed to update item', 'error');
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await boqService.deleteItem(itemId);
      await loadDocument(activeDoc.id);
    } catch (err) {
      showSnack('Failed to delete item', 'error');
    }
  };

  const handleAddItem = async (categoryId, itemType) => {
    try {
      if (itemType === 'labour') {
        await boqService.addItem(categoryId, {
          item_name: 'New Labour',
          unit_price: 0,
          item_type: 'labour'
        });
      } else {
        await boqService.addItem(categoryId, {
          item_name: 'New Item',
          quantity: 0,
          unit: 'bags',
          unit_price: 0
        });
      }
      await loadDocument(activeDoc.id);
    } catch (err) {
      showSnack('Failed to add item', 'error');
    }
  };

  const handleAddCategory = async () => {
    try {
      await boqService.addCategory(activeDoc.id, { name: 'NEW CATEGORY', labor_cost: 0 });
      await loadDocument(activeDoc.id);
    } catch (err) {
      showSnack('Failed to add category', 'error');
    }
  };

  const handleUpdateCategory = async (categoryId, data) => {
    try {
      await boqService.updateCategory(categoryId, data);
      await loadDocument(activeDoc.id);
    } catch (err) {
      showSnack('Failed to update category', 'error');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    try {
      await boqService.deleteCategory(categoryId);
      await loadDocument(activeDoc.id);
    } catch (err) {
      showSnack('Failed to delete category', 'error');
    }
  };

  const handleSubmitForReview = async () => {
    try {
      await boqService.updateDocument(activeDoc.id, { status: 'pending_acknowledgement' });
      await loadDocument(activeDoc.id);
      showSnack('Submitted for review');
    } catch (err) {
      showSnack('Failed to submit', 'error');
    }
  };

  // ── AI draft (create mode) ───────────────────────────────────────────────
  const handleDraftWithAI = async () => {
    try {
      setAiDrafting(true);
      setAiDraftError('');
      const result = await boqService.draftWithAI(projectId);
      const categories = result?.categories || [];
      if (categories.length === 0) {
        setAiDraftError('AI could not produce a draft for this brief. Please fill in the schedule manually.');
        return;
      }
      setAiDraft(categories);
    } catch (err) {
      setAiDraftError(err.response?.data?.error || 'AI drafting is unavailable right now. Please fill in the schedule manually.');
    } finally {
      setAiDrafting(false);
    }
  };

  // ── BOQ → merchant quote request ─────────────────────────────────────────
  const buildQuoteMaterials = useCallback(() => {
    const map = {};
    (activeDoc?.categories || []).forEach((cat) => (cat.items || []).forEach((item) => {
      if ((item.item_type || 'material') === 'labour') return;
      const remaining = parseFloat(item.quantity || 0) - parseFloat(item.quantity_purchased || 0);
      if (remaining <= 0) return;
      const categoryId = classifyQuoteCategory(item.item_name);
      if (!map[categoryId]) map[categoryId] = [];
      map[categoryId].push({
        materialName: item.item_name,
        quantity: remaining,
        unit: item.unit,
        notes: item.notes || '',
      });
    }));
    return map;
  }, [activeDoc]);

  const handleOpenQuoteWizard = async () => {
    try {
      const res = await api.get(`/projects/${projectId}`);
      const project = res.data?.data?.project || res.data?.data || res.data;
      setQuoteProject(project);
      setQuoteMaterials(buildQuoteMaterials());
      setQuoteWizardOpen(true);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to open the quote request', severity: 'error' });
    }
  };

  const handleAddComment = async (content) => {
    if (!activeDoc) return;
    try {
      setCommentLoading(true);
      await boqService.addComment(activeDoc.id, { target_type: 'document', content });
      const thread = await boqService.getComments(activeDoc.id);
      setComments(Array.isArray(thread) ? thread : []);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to post comment', severity: 'error' });
    } finally {
      setCommentLoading(false);
    }
  };

  const handleAcknowledge = async () => {
    try {
      await boqService.acknowledgeDocument(activeDoc.id);
      await loadDocument(activeDoc.id);
      showSnack('Schedule acknowledged and activated');
    } catch (err) {
      showSnack('Failed to acknowledge', 'error');
    }
  };

  const handleAcknowledgeChanges = async (changeIds) => {
    try {
      await boqService.acknowledgeChanges(changeIds);
      await loadDocument(activeDoc.id);
      setChangesOpen(false);
      showSnack('Changes acknowledged');
    } catch (err) {
      showSnack('Failed to acknowledge changes', 'error');
    }
  };

  const handleRejectChange = async (changeId, reason) => {
    try {
      await boqService.rejectChange(changeId, reason);
      await loadDocument(activeDoc.id);
      showSnack('Change rejected and reverted');
    } catch (err) {
      showSnack('Failed to reject change', 'error');
    }
  };

  const handleLogLaborPayment = async (categoryId, data) => {
    try {
      await boqService.logLaborPayment(categoryId, data);
      await loadDocument(activeDoc.id);
      showSnack('Labour payment logged');
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to log labour payment';
      showSnack(msg, 'error');
      throw err;
    }
  };

  const handleLoadLaborPayments = async (categoryId) => {
    try {
      return await boqService.getCategoryLaborPayments(categoryId);
    } catch (err) {
      showSnack('Failed to load labour payment history', 'error');
      return [];
    }
  };

  // ─── LIST VIEW ───
  if (!boqId) {
    return (
      <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link href={`/projects/${projectId}`} underline="hover" color="text.secondary">Project</Link>
          <Typography color="text.primary" fontWeight={600}>Materials Schedules</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>Stage BOQs / Materials & Labour Schedules</Typography>
            <Typography variant="body2" color="text.secondary">
              Track materials, labour costs, and purchases by work stage
            </Typography>
          </Box>
          {canManage && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate(`/projects/${projectId}/boq/new`)}
              sx={{ bgcolor: brand.primary, '&:hover': { bgcolor: brand.primaryDark }, borderRadius: 2, px: 3 }}
            >
              New Stage BOQ
            </Button>
          )}
        </Box>

        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, borderColor: '#e5e7eb' }}>
          <TextField
            select
            size="small"
            label="Filter by work stage"
            value={selectedStageId}
            onChange={(e) => setSelectedStageId(e.target.value)}
            sx={{ minWidth: { xs: '100%', sm: 320 } }}
          >
            <MenuItem value="">All project schedules</MenuItem>
            {workStages.map(stage => (
              <MenuItem key={stage.id} value={stage.id}>
                {stage.milestoneDetails?.name || stage.name}
              </MenuItem>
            ))}
          </TextField>
        </Paper>

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress sx={{ color: brand.primary }} /></Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : boqList.length === 0 ? (
          <Paper sx={{ textAlign: 'center', py: 6, px: 3, borderRadius: 3, border: '2px dashed #e5e7eb' }}>
            <DescriptionIcon sx={{ fontSize: 48, color: brand.primaryLight, mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              No Stage BOQ Shared Yet
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              {canManage
                ? 'Build the materials, quantities, labour, and expected costs for a work stage, then submit it to the owner for sign-off before procurement.'
                : 'Your assigned contractor will prepare the stage BOQ and submit it here for your review.'}
            </Typography>
            {canManage && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate(`/projects/${projectId}/boq/new`)}
                sx={{ bgcolor: brand.primary, '&:hover': { bgcolor: brand.primaryDark }, borderRadius: 2 }}
              >
                Build Stage BOQ
              </Button>
            )}
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {boqList.map(doc => {
              const sc = statusConfig[doc.status] || statusConfig.draft;
              return (
                <Grid item xs={12} sm={6} key={doc.id}>
                  <Card sx={{
                    borderRadius: 3, border: '1px solid #e5e7eb',
                    cursor: 'pointer', transition: 'all 0.2s',
                    '&:hover': { borderColor: brand.primary, boxShadow: `0 4px 12px ${brand.primary}15` }
                  }}
                    onClick={() => navigate(`/projects/${projectId}/boq/${doc.id}`)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <DescriptionIcon sx={{ color: brand.primary }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>{doc.title}</Typography>
                        <Chip label={sc.label} size="small" sx={{ bgcolor: sc.bg, color: sc.color, fontWeight: 600 }} />
                      </Box>
                      <Chip
                        label={doc.milestone_name ? `Stage: ${doc.milestone_name}` : 'Whole project'}
                        size="small"
                        variant="outlined"
                        sx={{ mb: 1.5, fontSize: '0.7rem' }}
                      />
                      <Grid container spacing={2}>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">Materials</Typography>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>${fmt(doc.total_materials)}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">Labour</Typography>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>${fmt(doc.total_labor)}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">Total</Typography>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700, color: brand.primaryDark }}>${fmt(doc.grand_total)}</Typography>
                        </Grid>
                      </Grid>
                      <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                        <Chip label={`${doc.category_count} categories`} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        <Chip label={`${doc.item_count} items`} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        {doc.pending_changes > 0 && (
                          <Chip label={`${doc.pending_changes} pending changes`} size="small"
                            sx={{ bgcolor: statusColors.warningBg, color: statusColors.warningDark, fontWeight: 600, fontSize: '0.7rem' }} />
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}

        <Snackbar open={snackbar.open} autoHideDuration={4000}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))} message={snackbar.message} />
      </Box>
    );
  }

  // ─── CREATE MODE ───
  if (createMode) {
    if (!canManage) {
      return (
        <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
          <Button startIcon={<ArrowBackIcon />} size="small"
            onClick={() => navigate(`/projects/${projectId}`)}
            sx={{ color: 'text.secondary', mb: 2 }}>
            Back to Project
          </Button>
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            Materials schedules are prepared by the assigned contractor. Request a schedule from the project page, then review it here once submitted.
          </Alert>
        </Box>
      );
    }

    return (
      <Box sx={{ maxWidth: 1400, mx: 'auto', p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Button startIcon={<ArrowBackIcon />} size="small"
            onClick={() => { setCreateMode(false); navigate(`/projects/${projectId}`); }}
            sx={{ color: 'text.secondary' }}>
            Back to Project
          </Button>
        </Box>

        {/* Inline title input */}
        <Box sx={{ mb: 2 }}>
          <TextField
            placeholder="Enter BOQ name (e.g. Siting, Excavation and Foundation)"
            value={createTitle}
            onChange={(e) => setCreateTitle(e.target.value)}
            fullWidth
            variant="standard"
            InputProps={{
              disableUnderline: true,
              sx: { fontSize: '1.5rem', fontWeight: 700, color: brand.primaryDark }
            }}
            sx={{ mb: 0.5 }}
          />
          <Typography variant="body2" color="text.secondary">
            Choose the stage this BOQ belongs to, fill in the spreadsheet, then click "Save Schedule".
          </Typography>
        </Box>

        <TextField
          select
          label="Work stage"
          value={createStageId}
          onChange={(e) => setCreateStageId(e.target.value)}
          fullWidth
          helperText="Optional, but recommended. This links the BOQ to the stage the owner is approving."
          sx={{ mb: 2 }}
        >
          <MenuItem value="">Whole project / not stage-specific</MenuItem>
          {workStages.map(stage => (
            <MenuItem key={stage.id} value={stage.id}>
              {stage.milestoneDetails?.name || stage.name}
            </MenuItem>
          ))}
        </TextField>

        <Alert severity="info" variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
          This schedule is fully customizable. Click on any category name to rename it (e.g. "CONCRETE SLAB", "ROOFING", "TILING").
          Use <strong>"Add Category"</strong> to add more sections and <strong>"Add Item"</strong> to add materials within each category.
          Delete categories or items you don't need. Make it match your project.
        </Alert>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={aiDrafting ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
            disabled={aiDrafting}
            onClick={handleDraftWithAI}
            sx={{ borderColor: brand.primary, color: brand.primary, borderRadius: 2 }}
          >
            {aiDrafting ? 'Drafting…' : 'Draft with AI'}
          </Button>
          <Typography variant="caption" color="text.secondary">
            Generates a starting schedule from the project brief — review quantities and prices before saving.
          </Typography>
        </Box>
        {aiDraftError && (
          <Alert severity="warning" onClose={() => setAiDraftError('')} sx={{ mb: 2, borderRadius: 2 }}>
            {aiDraftError}
          </Alert>
        )}

        <BOQSpreadsheet
          isCreateMode={true}
          title={createTitle}
          onSave={handleSaveNewSchedule}
          saving={createLoading}
          seedCategories={aiDraft}
        />

        <Snackbar open={snackbar.open} autoHideDuration={4000}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))} message={snackbar.message} />
      </Box>
    );
  }

  // ─── DOCUMENT VIEW ───
  if (loading) {
    return <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress sx={{ color: brand.primary }} /></Box>;
  }

  if (error || !activeDoc) {
    return <Box sx={{ p: 3 }}><Alert severity="error">{error || 'Document not found'}</Alert></Box>;
  }

  const sc = statusConfig[activeDoc.status] || statusConfig.draft;
  const isCreator = activeDoc.created_by === user?.id;
  // The assigned PM can author/submit/edit schedules alongside the creator.
  const canManageDoc = isCreator || isProjectManager;
  const canSubmit = activeDoc.status === 'draft' && canManageDoc;
  const canAcknowledge = activeDoc.status === 'pending_acknowledgement' && !isCreator;
  const canEditSchedule = canManageDoc && activeDoc.status !== 'active';

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Breadcrumbs sx={{ mb: 1 }}>
        <Link href={`/projects/${projectId}`} underline="hover" color="text.secondary">Project</Link>
        <Link component="button" underline="hover" color="text.secondary"
          onClick={() => navigate(`/projects/${projectId}/boq`)}>
          Materials Schedules
        </Link>
        <Typography color="text.primary" fontWeight={600}>{activeDoc.title}</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>{activeDoc.title}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <Chip label={sc.label} size="small" sx={{ bgcolor: sc.bg, color: sc.color, fontWeight: 600 }} />
            <Chip
              label={activeDoc.milestone_name ? `Stage: ${activeDoc.milestone_name}` : 'Whole project'}
              size="small"
              variant="outlined"
            />
            <Typography variant="caption" color="text.secondary">
              v{activeDoc.version} · {new Date(activeDoc.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {pendingChanges.length > 0 && (
            <Badge badgeContent={pendingChanges.length} color="warning">
              <Button variant="outlined" startIcon={<CompareArrowsIcon />} onClick={() => setChangesOpen(true)}
                sx={{ borderColor: statusColors.warning, color: statusColors.warningDark, borderRadius: 2 }}>
                Review Changes
              </Button>
            </Badge>
          )}
          {user?.role === 'client' && activeDoc.status === 'active' && Object.keys(buildQuoteMaterials()).length > 0 && (
            <Button variant="outlined" startIcon={<StorefrontIcon />} onClick={handleOpenQuoteWizard}
              sx={{ borderColor: brand.primary, color: brand.primary, borderRadius: 2 }}>
              Request Merchant Quotes
            </Button>
          )}
          {canSubmit && (
            <Button variant="contained" startIcon={<SendIcon />} onClick={handleSubmitForReview}
              sx={{ bgcolor: brand.primary, '&:hover': { bgcolor: brand.primaryDark }, borderRadius: 2 }}>
              Submit to Client for Review
            </Button>
          )}
          {canAcknowledge && (
            <Button variant="contained" startIcon={<CheckCircleIcon />} onClick={handleAcknowledge}
              sx={{ bgcolor: statusColors.success, '&:hover': { bgcolor: statusColors.successDark }, borderRadius: 2 }}>
              Acknowledge & Activate
            </Button>
          )}
        </Box>
      </Box>

      {canSubmit && (
        <Alert severity="info" variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
          This is still a draft — your client won't see it yet. Click "Submit to Client for Review" when you're ready for sign-off.
        </Alert>
      )}

      {!isCreator && activeDoc.status === 'pending_acknowledgement' && (
        <Alert severity="warning" variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
          Review the contractor's materials schedule. If it looks right, acknowledge it to activate the schedule before procurement begins.
        </Alert>
      )}

      {/* Unmatched banner */}
      {unifiedSummary?.integration_status?.unmatched_items > 0 && (
        <BOQUnmatchedBanner
          boqId={activeDoc.id}
          unmatchedCount={unifiedSummary.integration_status.unmatched_items}
          boqItems={(activeDoc.categories || []).flatMap(c => c.items || [])}
          onLoadUnmatched={() => boqService.getUnmatchedItems(activeDoc.id)}
          onLink={(data) => boqService.linkOrderItem(data)}
          onRefresh={() => loadDocument(activeDoc.id)}
        />
      )}

      {/* Tabs */}
      <Paper variant="outlined" sx={{ mb: 2, borderRadius: 2, borderColor: '#e5e7eb' }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.85rem' },
            '& .Mui-selected': { color: brand.primary },
            '& .MuiTabs-indicator': { bgcolor: brand.primary }
          }}
        >
          <Tab label="Procurement Tracker" />
          <Tab label="Purchase Log" />
          <Tab label="Summary" />
          <Tab
            label={
              <Badge badgeContent={comments.length} color="primary" max={99}
                sx={{ '& .MuiBadge-badge': { right: -10, top: 2 } }}>
                Discussion
              </Badge>
            }
          />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 && (
        <BOQSpreadsheet
          categories={activeDoc.categories}
          title={activeDoc.title}
          metadata={{
            contractor: activeDoc.creator_profile ? `${activeDoc.creator_profile.first_name} ${activeDoc.creator_profile.last_name}` : '',
            date: new Date(activeDoc.created_at).toLocaleDateString('en-GB'),
            stage: activeDoc.description || ''
          }}
          onUpdateItem={handleUpdateItem}
          onDeleteItem={handleDeleteItem}
          onAddItem={handleAddItem}
          onAddCategory={handleAddCategory}
          onUpdateCategory={handleUpdateCategory}
          onDeleteCategory={handleDeleteCategory}
          onLogLaborPayment={handleLogLaborPayment}
          onLoadLaborPayments={handleLoadLaborPayments}
          readOnly={!canEditSchedule}
        />
      )}

      {activeTab === 1 && (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Purchase Log</Typography>
          {allPurchases.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No purchases logged yet. Use the "Qty Bought" column in the Procurement Tracker to log purchases.
            </Typography>
          ) : (
            <BOQPurchaseHistory purchases={allPurchases} />
          )}
        </Paper>
      )}

      {activeTab === 2 && (
        <Box>
          {/* Budget Stats */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'Materials Budget', value: activeDoc.summary?.total_materials, color: brand.primary },
              { label: 'Labour Budget', value: activeDoc.summary?.total_labor, color: brand.secondary },
              { label: 'Total Budget', value: activeDoc.summary?.grand_total, color: brand.primaryDark },
            ].map((stat) => (
              <Grid item xs={12} sm={4} key={stat.label}>
                <Paper sx={{ p: 2, borderRadius: 2, borderLeft: `4px solid ${stat.color}` }}>
                  <Typography variant="caption" color="text.secondary">{stat.label}</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: 'monospace', color: stat.color }}>
                    ${fmt(stat.value)}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Unified Summary Table */}
          {unifiedSummary ? (
            <BOQSummaryTable categories={unifiedSummary.categories} summary={unifiedSummary.totals} unified={true} />
          ) : (
            <BOQSummaryTable categories={activeDoc.categories} summary={activeDoc.summary} />
          )}
        </Box>
      )}

      {activeTab === 3 && (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, maxWidth: 720 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Discussion</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Talk through items, prices, and substitutions here so decisions stay attached to the schedule.
          </Typography>
          <BOQCommentThread
            comments={comments}
            onAddComment={handleAddComment}
            loading={commentLoading}
          />
        </Paper>
      )}

      {/* Changes Review Dialog */}
      <BOQChangesReviewDialog
        open={changesOpen}
        onClose={() => setChangesOpen(false)}
        changes={pendingChanges}
        onAcknowledge={handleAcknowledgeChanges}
        onReject={handleRejectChange}
      />

      {/* BOQ → merchant quote request (prefilled with unpurchased items) */}
      {quoteProject && (
        <QuoteRequestWizard
          open={quoteWizardOpen}
          onClose={(success) => {
            setQuoteWizardOpen(false);
            setQuoteProject(null);
            if (success) {
              setSnackbar({ open: true, message: 'Quote request sent to merchants', severity: 'success' });
            }
          }}
          project={quoteProject}
          initialMaterials={quoteMaterials}
        />
      )}

      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))} message={snackbar.message} />
    </Box>
  );
}
